// ============================================================================
// TYPE DECLARATION BREAKDOWN
// ============================================================================

/**
 * LayoutConfigLest (Note: Likely typo - should be "LayoutConfigList")
 *
 * Type Structure:
 *   Array<[number, number, number, number]>
 *
 * Breakdown:
 *   1. Array - A JavaScript/TypeScript array type
 *   2. [number, number, number, number] - A tuple type with exactly 4 elements
 *      - All elements must be of type `number`
 *      - The tuple has a fixed length of 4 (not variable)
 *      - Order matters: [x, y, w, h]
 *
 * Tuple Element Meanings:
 *   [0] - x: number  → Horizontal position (column index, 0-based)
 *   [1] - y: number  → Vertical position (row index, 0-based)
 *   [2] - w: number  → Width (number of columns the item spans)
 *   [3] - h: number  → Height (number of rows the item spans)
 *
 * Example:
 *   [0, 0, 8, 16] means:
 *     - Start at column 0, row 0
 *     - Span 8 columns wide
 *     - Span 16 rows tall
 *
 * Type Safety:
 *   - TypeScript enforces that each array element is a tuple of exactly 4 numbers
 *   - Prevents: wrong length, wrong types, missing elements
 *   - Allows: any number of tuples in the array
 */
export type LayoutDefinition = [number, number, number, number];
export type LayoutConfigLest = Array<LayoutDefinition>;

// ============================================================================
// CONSTANT DECLARATIONS
// ============================================================================

/**
 * defaultPredefinedLayouts
 *
 * Type: LayoutConfigLest
 * Purpose: Default layout configuration for 8 grid items (slots 0-7)
 *
 * Grid System Context:
 *   - Uses a 12-column grid system (based on react-grid-layout default)
 *   - Row height is 30px (defined in GridViewer defaultProps)
 *   - Each item represents a slot that can display external content
 *
 * Layout Analysis:
 *   Slot 0: [0, 0, 8, 16]  → Left side, full height, 8 cols wide
 *   Slot 1: [8, 0, 2, 16]  → Right side, full height, 2 cols wide
 *   Slot 2: [0, 31, 3, 8]  → Bottom left, 3 cols, 8 rows
 *   Slot 3: [0, 16, 3, 15] → Middle left, 3 cols, 15 rows
 *   Slot 4: [3, 16, 7, 7]  → Middle center, 7 cols, 7 rows
 *   Slot 5: [3, 23, 7, 8]  → Middle bottom, 7 cols, 8 rows
 *   Slot 6: [5, 31, 7, 6]  → Bottom right, 7 cols, 6 rows
 *   Slot 7: [3, 39, 7, 6]  → Bottom center, 7 cols, 6 rows
 */
export const defaultPredefinedLayouts: LayoutConfigLest = [
  [0, 4, 8, 16],
  [8, 4, 2, 16],
  [0, 40, 3, 27],
  [0, 20, 3, 20],
  [3, 20, 7, 13],
  [3, 33, 7, 8],
  [3, 41, 7, 6],
  [3, 47, 2, 16],
  [0, 0, 10, 4],
];

/**
 * widthfit
 *
 * Type: LayoutConfigLest
 * Purpose: Alternative layout configuration optimized for width fitting
 *
 * Layout Differences from defaultPredefinedLayouts:
 *   - Different positioning strategy
 *   - Items arranged to maximize width usage
 *   - Different vertical distribution
 *
 * Layout Analysis:
 *   Slot 0: [0, 0, 8, 16]  → Same as default
 *   Slot 1: [8, 0, 2, 16]  → Same as default
 *   Slot 2: [8, 39, 2, 11] → Right side bottom, 2 cols, 11 rows
 *   Slot 3: [8, 16, 2, 23] → Right side middle, 2 cols, 23 rows
 *   Slot 4: [0, 16, 8, 7]  → Left side middle, full width (8 cols), 7 rows
 *   Slot 5: [0, 23, 8, 8]  → Left side lower, full width, 8 rows
 *   Slot 6: [0, 31, 8, 6]  → Left side bottom, full width, 6 rows
 *   Slot 7: [0, 37, 8, 6]  → Left side lowest, full width, 6 rows
 */
export const widthfit: LayoutConfigLest = [
  [0, 4, 8, 16],
  [8, 4, 2, 16],
  [9, 20, 3, 27],
  [0, 20, 3, 20],
  [3, 20, 6, 13],
  [0, 40, 9, 7],
  [3, 33, 6, 7],
  [10, 4, 2, 16],
  [0, 0, 12, 4],
];
export const rightTrade: LayoutConfigLest = [
  [0, 4, 8, 16],
  [8, 4, 2, 16],
  [0, 20, 3, 27],
  [10, 4, 2, 20],
  [3, 20, 7, 13],
  [3, 33, 9, 7],
  [3, 40, 9, 12],
  [0, 47, 3, 19],
  [0, 0, 12, 4],
];
export const testTrade: LayoutConfigLest = [
  [0, 48, 8, 16],
  [5, 4, 2, 20],
  [3, 4, 2, 20],
  [7, 4, 3, 20],
  [0, 24, 10, 12],
  [0, 64, 10, 8],
  [0, 36, 10, 12],
  [0, 4, 3, 20],
  [0, 0, 10, 4],
];
export const triPanel: LayoutConfigLest = [
  [0, 64, 8, 16],
  [5, 4, 2, 20],
  [0, 24, 2, 20],
  [7, 4, 3, 20],
  [2, 24, 8, 20],
  [0, 80, 10, 8],
  [0, 4, 5, 20],
  [0, 44, 3, 20],
  [0, 0, 10, 4],
];
// ============================================================================
// SLOT CONSTANTS
// ============================================================================

/**
 * EXTERNAL_SLOT_START
 *
 * Type: number (literal type: 0)
 * Purpose: Defines the starting index for external component slots
 *
 * Usage Context:
 *   - Used in GridViewer to determine which grid items are "external slots"
 *   - External slots can receive React components via slotContent prop
 *   - Range: EXTERNAL_SLOT_START (0) to EXTERNAL_SLOT_END (7) inclusive
 *
 * Type Breakdown:
 *   - Literal type: TypeScript infers this as type `0`, not `number`
 *   - Can be used in type-level operations
 *   - Provides compile-time safety for slot indexing
 */
export const EXTERNAL_SLOT_START = 0; // First external slot index

/**
 * EXTERNAL_SLOT_END
 *
 * Type: number (literal type: 7)
 * Purpose: Defines the ending index for external component slots
 *
 * Usage Context:
 *   - Used with EXTERNAL_SLOT_START to define valid slot range
 *   - GridViewer checks: i >= EXTERNAL_SLOT_START && i <= EXTERNAL_SLOT_END
 *   - This means slots 0-7 (8 total slots) can receive external content
 *
 * Type Breakdown:
 *   - Literal type: TypeScript infers this as type `7`, not `number`
 *   - Creates a type-level constraint for slot indexing
 *   - Both constants together define a closed interval [0, 7]
 *
 * Relationship:
 *   - Total slots: EXTERNAL_SLOT_END - EXTERNAL_SLOT_START + 1 = 8 slots
 *   - Matches the length of defaultPredefinedLayouts array (8 items)
 */
export const EXTERNAL_SLOT_END = 8; // Last external slot index

// ============================================================================
// TYPE RELATIONSHIPS
// ============================================================================

/**
 * Type Flow:
 *
 * LayoutConfigLest (Array of tuples)
 *     ↓
 * defaultPredefinedLayouts / widthfit (concrete instances)
 *     ↓
 * generateLayout() function (util.ts)
 *     ↓
 * LayoutItem[] (extends react-grid-layout's Layout)
 *     ↓
 * ResponsiveLayouts (react-grid-layout type)
 *     ↓
 * GridViewer component state
 *
 * Type Safety Chain:
 *   1. LayoutConfigLest ensures tuple structure [x, y, w, h]
 *   2. createLayoutItemFromConfig() converts tuple to LayoutItem
 *   3. LayoutItem extends Layout with optional static property
 *   4. ResponsiveLayouts maps breakpoint names to LayoutItem[]
 *   5. GridViewer uses ResponsiveLayouts in state management
 */
